import { PacienteDoPedido } from "./PacienteDoPedido";
import { ArrowDown, ArrowUp } from "lucide-react";
import { cn } from "@/lib/cn";
import { formatBRL, formatDate } from "./prosthesis-meta";
import { DENTIST_STATUS_META, dentistStatusBucket } from "./dentist-status";
import { ProsthesisOrderServiceLines } from "./ProsthesisOrderServiceLines";
import type { OrderSortField, SortDirection } from "./prosthesis-orders-sort";
import type { ProsthesisOrderRow } from "./types";
import { totalParaExibir } from "@/components/prosthesis/valor-do-pedido";

type Props = {
  orders: ProsthesisOrderRow[];
  onOpen: (id: string) => void;
  sortField: OrderSortField;
  sortDirection: SortDirection;
  onSort: (field: OrderSortField) => void;
};

export function ProsthesisOrdersListView({ orders, onOpen, sortField, sortDirection, onSort }: Props) {
  return (
    <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white dark:border-border dark:bg-card">
      <table className="w-full text-sm">
        <thead className="border-b border-zinc-100 bg-zinc-50/50 text-[11px] uppercase tracking-wider text-zinc-500 dark:border-border dark:bg-background/50 dark:text-muted-foreground">
          <tr>
            <SortableHeader field="patient" label="Paciente" active={sortField} direction={sortDirection} onSort={onSort} className="px-3 py-2.5 text-left" />
            <th className="px-2 py-2.5 text-left font-semibold">Serviços</th>
            <SortableHeader
              field="lab"
              label="Laboratório"
              active={sortField}
              direction={sortDirection}
              onSort={onSort}
              className="hidden px-2 py-2.5 text-left lg:table-cell"
            />
            <SortableHeader
              field="createdAt"
              label="Data do pedido"
              active={sortField}
              direction={sortDirection}
              onSort={onSort}
              className="hidden px-2 py-2.5 text-left md:table-cell"
            />
            <SortableHeader
              field="deadline"
              label="Prazo"
              active={sortField}
              direction={sortDirection}
              onSort={onSort}
              className="hidden px-2 py-2.5 text-left md:table-cell"
            />
            <th className="hidden px-2 py-2.5 text-right font-semibold xl:table-cell">Valor total</th>
            <SortableHeader field="status" label="Status" active={sortField} direction={sortDirection} onSort={onSort} className="px-2 py-2.5 text-left" />
          </tr>
        </thead>
        <tbody>
          {orders.map((o) => {
            const total = totalParaExibir(o.services, o.acrescimos);
            const bucket = dentistStatusBucket(o.status);
            return (
              <tr
                key={o.id}
                onClick={() => onOpen(o.id)}
                className="cursor-pointer border-b border-zinc-100 transition-colors hover:bg-zinc-50 dark:border-border/50 dark:hover:bg-muted/50"
              >
                <td className="px-3 py-2.5 font-medium text-zinc-900 dark:text-foreground">
                  <PacienteDoPedido order={o} />
                </td>
                <td className="px-2 py-2.5 text-zinc-700 dark:text-foreground/80">
                  <ProsthesisOrderServiceLines services={o.services} />
                </td>
                <td className="hidden px-2 py-2.5 text-zinc-700 dark:text-foreground/80 lg:table-cell">
                  {o.labName || "—"}
                </td>
                <td className="hidden px-2 py-2.5 tabular-nums text-zinc-700 dark:text-foreground/80 md:table-cell">
                  {formatDate(o.createdAt)}
                </td>
                <td className="hidden px-2 py-2.5 tabular-nums text-zinc-700 dark:text-foreground/80 md:table-cell">
                  {formatDate(o.deadline)}
                </td>
                <td className="hidden px-2 py-2.5 text-right tabular-nums text-zinc-700 dark:text-foreground/80 xl:table-cell">
                  {formatBRL(total)}
                </td>
                <td className="px-2 py-2.5">
                  <span
                    className="inline-flex rounded-full px-2 py-1 text-[11px] font-bold uppercase"
                    style={{ backgroundColor: DENTIST_STATUS_META[bucket].bg, color: DENTIST_STATUS_META[bucket].color }}
                  >
                    {DENTIST_STATUS_META[bucket].label}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Cabeçalho clicável — clica pra ordenar por essa coluna, clica de novo inverte a direção.
 *  Mais direto que um menu de campos pra quem não tá acostumado com software. */
function SortableHeader({
  field,
  label,
  active,
  direction,
  onSort,
  className,
}: {
  field: OrderSortField;
  label: string;
  active: OrderSortField;
  direction: SortDirection;
  onSort: (field: OrderSortField) => void;
  className?: string;
}) {
  const isActive = active === field;
  return (
    <th className={className}>
      <button
        type="button"
        onClick={() => onSort(field)}
        className={cn(
          "inline-flex items-center gap-1 whitespace-nowrap font-semibold uppercase tracking-wider transition-colors hover:text-zinc-900 dark:hover:text-foreground",
          isActive && "text-brand-700 dark:text-primary",
        )}
      >
        {label}
        {isActive &&
          (direction === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
      </button>
    </th>
  );
}
